import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios, { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Badge, Col, Row } from 'react-bootstrap';
import { Form, Input, Label, Table } from 'reactstrap';
import { Button as ChakraButton } from '@chakra-ui/react';
import { FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

import InfinitePagination from '../common/InfinitePagination';
import AuthenticationHistorySearchBar, { SearchValues } from './AuthenticationHistorySearchBar';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { ApplicationState } from '../../store';
import { CompanyMaster } from '../../models/CompanyMaster';
import { useAdminContext } from '../../context/AdminProvider';
import * as CompanyMasterStore from '../../store/CompanyMaster';

type ListResponse<T> = {
    totalCount: number;
    resultList: T[];
};

interface AuthenticationHistory {
    citizenId: string;
    actionId: string;
    action: 'login';
    actionResponse: 'success' | 'fail';
    actionMessage?: string;
    timestamp: string;
    ipAddress: string;
    clientIPAddress: string;
    keyUUID: string;
    mode: string;
}

type SelectOption = { value: string; label: string };

function AuthenticationHistoryLogs() {
    const dispatch = useDispatch();
    const companies = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );
    const { userRole } = useAdminContext();

    useEffect(() => {
        dispatch(CompanyMasterStore.actionCreators.requestFunctionCompanyMaster(true, 'GET', '', 'active', '') as any);
    }, []);

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowedSids = new Set(userRole.map(r => r.sid));
        return companies.filter(c => allowedSids.has(c.sid)).map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;

    const [hasSearched, setHasSearched] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [selectedSid, setSelectedSid] = useState<string>('');
    const [sid, setSid] = useState<string | undefined>();
    const [citizenId, setCitizenId] = useState<string>();
    const [from, setFrom] = useState<Date | null>();
    const [to, setTo] = useState<Date | null>();

    // Auto-select when company is locked (only 1 company available)
    useEffect(() => {
        if (isCompanyLocked && companyOptions.length === 1) {
            const lockedSid = companyOptions[0].value;
            setSelectedSid(lockedSid);
            setSid(lockedSid);
        }
    }, [isCompanyLocked, companyOptions]);

    const fromISO = useMemo(() => (!from ? undefined : from.toISOString()), [from]);
    const toISO = useMemo(() => (!to ? undefined : to.toISOString()), [to]);

    const { error, data, isFetching } = useQuery({
        queryKey: ['auth-history', { page, pageSize, sid, citizenId, fromISO, toISO }],
        queryFn: () =>
            axios.get<ListResponse<AuthenticationHistory>>('/v1/auth-history', {
                headers: { Authorization: `Bearer ${localStorage.getItem('DMS_APPLICATION')}` },
                params: { page, page_size: pageSize, sid, citizen_id: citizenId, from_date: fromISO, to_date: toISO },
            }),
        select: (response) => response.data.resultList,
        placeholderData: prev => prev,
        enabled: hasSearched,
    });

    useEffect(() => {
        if (!error) return;
        if (error instanceof AxiosError) {
            Swal.fire('Error', error.response?.data.message || 'ไม่ทราบสาเหตุ');
        } else if (error instanceof Error) {
            Swal.fire('Error', error.message);
        }
    }, [error]);

    const handleSidChange = useCallback((value: string) => {
        setSelectedSid(value);
    }, []);

    const handleSubmitSearch = useCallback((values: SearchValues) => {
        setPage(1);
        setSid(values.sid || undefined);
        setFrom(values.from);
        setTo(values.to);
        setCitizenId(values.citizenId);
        setHasSearched(true);
    }, []);

    const [isExporting, setIsExporting] = useState(false);

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            const response = await axios.get<ListResponse<AuthenticationHistory>>('/v1/auth-history', {
                headers: { Authorization: `Bearer ${localStorage.getItem('DMS_APPLICATION')}` },
                params: { page: 1, page_size: 100000, sid, citizen_id: citizenId, from_date: fromISO, to_date: toISO },
            });

            const rows = response.data.resultList;
            if (!rows || rows.length === 0) {
                Swal.fire('ไม่พบข้อมูล', 'ไม่มีข้อมูลที่จะ Export', 'warning');
                return;
            }

            const sheetData = rows.map(log => ({
                'Mode': log.mode?.toUpperCase() ?? '',
                'User': log.citizenId ?? '',
                'Action': log.action ?? '',
                'Action Result': log.actionResponse ?? '',
                'Action Message': log.actionMessage ?? '',
                'Timestamp': log.timestamp ? dayjs(log.timestamp).format('DD/MM/YYYY HH:mm:ss') : '',
                'IP Address': log.ipAddress ?? '',
                'Client IP Address': log.clientIPAddress ?? '',
                'Key UUID': log.keyUUID ?? '',
            }));

            const ws = XLSX.utils.json_to_sheet(sheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'AuthHistory');
            XLSX.writeFile(wb, `AuthHistory_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
        } catch (err) {
            Swal.fire('Error', 'Export ไม่สำเร็จ', 'error');
        } finally {
            setIsExporting(false);
        }
    }, [sid, citizenId, fromISO, toISO]);

    return (
        <div>
            {/* Page title */}
            <div className="mt-2">
                <div className="trapezoid" style={{ width: 'auto', display: 'inline-block' }}>
                    <span className="employee-font font-size-employee">
                        ADM-DMS106 Authentication History
                    </span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <ChakraButton
                        colorScheme="green"
                        size="sm"
                        leftIcon={<FaFileExcel />}
                        isLoading={isExporting}
                        loadingText="Exporting..."
                        onClick={handleExport}
                    >
                        Export Excel
                    </ChakraButton>
                </div>
            </div>

            <div className="mat-box employee-font font-size-employee">
                <Row>
                    <Col>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>Search Menu</span>
                        </Row>
                        <AuthenticationHistorySearchBar
                            onSubmitSearch={handleSubmitSearch}
                            companyOptions={companyOptions}
                            isCompanyLocked={isCompanyLocked}
                            selectedSid={selectedSid}
                            onSidChange={handleSidChange}
                        />
                    </Col>
                </Row>

                <br />

                {/* Page size + Pagination */}
                <Row>
                    <Col xs={6}>
                        <div className="d-flex justify-content-start my-2 my-lg-0">
                            <Form inline>
                                <Label style={{ fontSize: '14px' }}>Show&nbsp;&nbsp;</Label>
                                <Input
                                    type="select"
                                    name="select"
                                    bsSize="sm"
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                >
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Input>
                                <Label style={{ fontSize: '14px' }}>&nbsp;&nbsp;entries</Label>
                            </Form>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex justify-content-end my-2 my-lg-0">
                            <InfinitePagination
                                disabledNext={!data || data.length === 0}
                                page={page}
                                style={{ padding: '0px', marginTop: '0px', justifyContent: 'flex-end' }}
                                onChangePage={(_event, p) => setPage(p)}
                            />
                        </div>
                    </Col>
                </Row>

                {/* Table */}
                <Row className="mt-2">
                    <Col>
                        <Table className="table-bordered align-middle employee-font font-size-employee" size="sm">
                            <thead>
                                <tr className="text-center table-active thead-light">
                                    <th>Mode</th>
                                    <th>User</th>
                                    <th>Action</th>
                                    <th>Action Result</th>
                                    <th>Action Message</th>
                                    <th>Timestamp</th>
                                    <th>IP Address</th>
                                    <th>Client IP Address</th>
                                    <th>Key UUID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!hasSearched ? (
                                    <tr>
                                        <td colSpan={9} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>
                                            กรุณากด ค้นหา เพื่อแสดงข้อมูล
                                        </td>
                                    </tr>
                                ) : isFetching ? (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: 'center' }}>
                                            Loading...
                                        </td>
                                    </tr>
                                ) : !data || data.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>
                                            No Data.
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((log) => (
                                        <tr key={log.actionId}>
                                            <td className="text-center">
                                                <Badge variant={log.mode === 'gui' ? 'primary' : 'info'}>
                                                    {log.mode.toUpperCase()}
                                                </Badge>
                                            </td>
                                            <td>{log.citizenId}</td>
                                            <td>{log.action}</td>
                                            <td className="text-center">
                                                <Badge variant={log.actionResponse === 'success' ? 'success' : 'danger'}>
                                                    {log.actionResponse}
                                                </Badge>
                                            </td>
                                            <td>{log.actionMessage}</td>
                                            <td>{dayjs(log.timestamp).format('DD/MM/YYYY HH:mm:ss น.')}</td>
                                            <td>{log.ipAddress}</td>
                                            <td>{log.clientIPAddress}</td>
                                            <td>{log.keyUUID}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </div>
        </div>
    );
}

export default AuthenticationHistoryLogs;
